/**
 * InvestorFitTags.js — Investor-Fit Overlay Tags
 * Displays indicative DFI/fund alignment tags from /lcfpi/investor-fit/{project_id}.
 * All tags are indicative/screened — never presented as confirmed or bankable.
 */
import React, { useState, useEffect } from "react";
import client from "@/api/client";
import { Loader, Info } from "lucide-react";

/**
 * Full investor-fit panel — for Investment Profile page.
 */
export function InvestorFitPanel({ projectId }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    client.get(`/lcfpi/investor-fit/${projectId}`)
      .then(r => setData(r.data)).catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [projectId]);

  if (loading) return (
    <div className="flex items-center gap-2 p-3">
      <Loader size={12} className="animate-spin text-slate-400" />
      <span className="text-xs text-slate-400">Computing investor-fit overlay…</span>
    </div>
  );
  if (!data || !data.tags?.length) return (
    <div className="p-3 text-xs text-slate-400">No investor-fit alignment identified at current stage.</div>
  );

  return (
    <div data-testid="investor-fit-panel">
      {/* Narrative */}
      <p className="text-xs text-slate-600 leading-relaxed mb-3">{data.investor_narrative}</p>

      {/* Tags grid */}
      <div className="flex flex-wrap gap-2 mb-3">
        {data.tags.map(tag => (
          <div key={tag.id}
            style={{ background: tag.bg, border: `1px solid ${tag.border}`, borderRadius: 8, padding: "4px 10px",
              display: "flex", alignItems: "center", gap: 6, cursor: "default" }}
            title={`${tag.basis}\n\n${tag.note}`}
            data-testid={`fit-tag-${tag.id}`}>
            <span style={{ fontSize: 10, fontWeight: 700, color: tag.color }}>{tag.label}</span>
            <span style={{ fontSize: 8, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase",
              background: "#f1f5f9", padding: "1px 4px", borderRadius: 3, letterSpacing: ".04em" }}>
              {tag.confidence}
            </span>
          </div>
        ))}
      </div>

      {/* Detail table */}
      <div className="space-y-2">
        {data.tags.map(tag => (
          <div key={tag.id} style={{ borderLeft: `3px solid ${tag.color}`, paddingLeft: 10, paddingTop: 2, paddingBottom: 2 }}>
            <p className="text-xs font-bold" style={{ color: tag.color }}>{tag.label}</p>
            <p className="text-xs text-slate-500 leading-relaxed">{tag.basis}</p>
            <p className="text-xs text-slate-400 italic">{tag.note}</p>
          </div>
        ))}
      </div>

      {/* Disclaimer */}
      <div className="flex items-start gap-2 mt-3 p-2 bg-slate-50 border border-slate-100 rounded-lg">
        <Info size={10} className="text-slate-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-slate-400 leading-relaxed">{data.confidence_note}</p>
      </div>
    </div>
  );
}

/**
 * Compact inline tag strip — for pipeline register and project cards.
 */
export function InvestorFitTagStrip({ projectId }) {
  const [tags, setTags] = useState(null);
  useEffect(() => {
    if (!projectId) return;
    client.get(`/lcfpi/investor-fit/${projectId}`)
      .then(r => setTags((r.data.tags || []).slice(0, 3))).catch(() => {});
  }, [projectId]);

  if (!tags?.length) return null;
  return (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }} data-testid={`fit-strip-${projectId}`}>
      {tags.map(t => (
        <span key={t.id} style={{ fontSize: 7, fontWeight: 700, color: t.color, background: t.bg,
          border: `1px solid ${t.border}`, padding: "1px 5px", borderRadius: 4 }}>
          {t.label}
        </span>
      ))}
    </div>
  );
}

export default InvestorFitPanel;
